/* eslint-disable react/no-unstable-nested-components */
import React, { useContext, useEffect } from 'react';

import { InfoOutlined, Replay } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useSession } from 'next-auth/react';
import NextLink from 'next/link';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';

import { track } from '../../modules/segment/helpers';
import { AccountOrdersContext } from '../../providers/AccountOrders';
import ReservationContext from '../../providers/ReservationContext';
import { longSentenceFormat } from '../../utils/datetime/formatDate';
import { standardTimeFormat } from '../../utils/datetime/formatTime';
import formatAddress from '../../utils/formatAddress';
import formatCurrency from '../../utils/monies/formatCurrency';
import getReservationTimeline from '../../utils/reservation/getReservationTimeline';
import Link from '../Link';
import VerticalDivider from './VerticalDivider';
import getServiceDisplayName from '../../utils/service/getServiceDisplayName';

dayjs.extend(tz);

const ReservationDetailsDesktop = () => {
  const { permissions, partner, orderV2Data } = useContext(ReservationContext);
  const { invoice } = orderV2Data ?? {};

  const { data: session, update } = useSession();
  const { data: accountOrders } = useContext(AccountOrdersContext);

  useEffect(() => {
    const timeline = getReservationTimeline({
      status: orderV2Data?.status,
      orderStartDateTime: orderV2Data?.start_date_time,
    });
    track('Dashboard Reservation Screen Loaded', {
      confirmationId: orderV2Data?.confirmation_id,
      orderId: orderV2Data?.id,
      timeline,
      guestLogin: session?.type !== 'oauth',
      startDateTime: dayjs(orderV2Data?.start_date_time).tz(orderV2Data?.start_time_zone).format('YYYY-MM-DD HH a'),
    });
  }, []);

  // Find packing addon from package service quotes
  const packingAddon = orderV2Data?.jobs?.find((job) => job.product_id === 'PACKINGSERVICE');

  const setMovingJob = packingAddon ? orderV2Data?.jobs?.[1] : orderV2Data?.jobs?.[0];

  const ScheduledPackageDisplay = () => (
    <>
      {packingAddon ? (
        <Typography variant='body2' color='textSecondary'>
          <b style={{ paddingRight: '4px' }}>Packing </b>
          {`${longSentenceFormat(dayjs(packingAddon.start_date_time).unix())} starting ${standardTimeFormat({
            datetime: dayjs(packingAddon.start_date_time).unix(),
            zone: packingAddon.start_timezone,
            createdDate: dayjs(orderV2Data.created_at).unix(),
          })}`}
        </Typography>
      ) : null}

      <Typography variant='body2' color='textSecondary'>
        <b style={{ paddingRight: '6px' }}>Moving </b>
        {`${longSentenceFormat(dayjs(setMovingJob.start_date_time).unix())} starting ${standardTimeFormat({
          datetime: dayjs(setMovingJob.start_date_time).unix(),
          zone: setMovingJob.start_timezone,
          createdDate: dayjs(orderV2Data.created_at).unix(),
        })}`}
      </Typography>
    </>
  );

  const ScheduledServicesDisplay = () =>
    orderV2Data?.jobs?.map((job) => {
      let serviceName = getServiceDisplayName({ id: job.product_id, name: 'Moving' });
      if (job.product_id === 'PACKINGSERVICE') {
        serviceName = 'Packing';
      }
      return (
        <Typography key={job.id} variant='body2' color='textSecondary'>
          <b style={{ paddingRight: '6px' }}>{serviceName} </b>
          {`${longSentenceFormat(dayjs(job.start_date_time).unix())} starting ${standardTimeFormat({
            datetime: dayjs(job.start_date_time).unix(),
            zone: job.start_timezone,
            createdDate: dayjs(orderV2Data?.created_at).unix(),
          })}`}
        </Typography>
      );
    });

  return (
    <Box sx={{ display: { xs: 'none', md: 'block' }, pt: 3 }}>
      {permissions?.showNonGuaranteed ? (
        <Box mb={2}>
          <Alert severity='success' icon={<InfoOutlined />}>
            Rushed Booking Request: We successfully assemble a team 95% of the time and will contact you with status updates.
          </Alert>
        </Box>
      ) : null}
      <Box display='flex' alignItems='center'>
        <Box mr='auto' flex={3}>
          <Typography variant='h4'>Order: {orderV2Data?.confirmation_id}</Typography>
        </Box>
        {session?.type === 'oauth' && session?.user?.email ? (
          <>
            <Box flex={6}>
              <Typography variant='h5' textAlign={accountOrders?.length > 1 ? 'center' : 'right'}>
                Logged in as: {session?.user?.email}
              </Typography>
            </Box>
            {accountOrders?.length > 1 ? (
              <Box flex={3}>
                <Button
                  LinkComponent={NextLink}
                  startIcon={<Replay />}
                  variant='contained'
                  href='/'
                  onClick={() => {
                    track('Dashboard - Desktop Home - Switch Order');
                    update({ action: 'switch_order' });
                  }}
                >
                  Switch Order
                </Button>
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          mt: 1,
          height: 160,
          backgroundColor: (theme) => theme.palette.neutral.lightest,
          border: '1px solid',
          borderColor: (theme) => theme.palette.neutral.light,
          borderRadius: '10px',
          width: '100%',
        }}
      >
        <Box display='flex' pl={3} pr={2} mt={3}>
          <Box display='flex' flexDirection='column'>
            <Typography variant='body1' color='secondary' fontFamily='CalibreMedium'>
              SCHEDULED SERVICES
            </Typography>
            {partner?.code === 'PODS' ? <ScheduledServicesDisplay /> : <ScheduledPackageDisplay />}
          </Box>
        </Box>
        <Box display='flex' pl={3} mt={3}>
          <Box display='flex' flexDirection='column' width={300} alignSelf='center'>
            <Typography variant='body1' color='secondary' fontFamily='CalibreMedium'>
              LOCATIONS
            </Typography>
            {orderV2Data?.locations?.length > 2 ? (
              <>
                <Typography noWrap variant='body2' color='textSecondary'>
                  {formatAddress({
                    line1: orderV2Data?.locations?.[0]?.street_address_1,
                    line2: orderV2Data?.locations?.[0]?.street_address_2,
                    city: orderV2Data?.locations?.[0]?.city,
                    region: orderV2Data?.locations?.[0]?.state,
                    postalCode: orderV2Data?.locations?.[0]?.postal_id,
                  })}
                </Typography>
                <Typography noWrap variant='body2' color='textSecondary'>
                  {formatAddress({
                    line1: orderV2Data?.locations?.[orderV2Data.locations.length - 1]?.street_address_1,
                    line2: orderV2Data?.locations?.[orderV2Data.locations.length - 1]?.street_address_2,
                    city: orderV2Data?.locations?.[orderV2Data.locations.length - 1]?.city,
                    region: orderV2Data?.locations?.[orderV2Data.locations.length - 1]?.state,
                    postalCode: orderV2Data?.locations?.[orderV2Data.locations.length - 1]?.postal_id,
                  })}
                </Typography>
              </>
            ) : (
              orderV2Data?.locations?.map((location) => (
                <Typography noWrap key={location.id} variant='body2' color='textSecondary'>
                  {formatAddress({
                    line1: location.street_address_1,
                    line2: location.street_address_2,
                    city: location.city,
                    region: location.state,
                    postalCode: location.postal_id,
                  })}
                </Typography>
              ))
            )}
          </Box>
        </Box>
        <Box pl={3} mt={3}>
          <VerticalDivider />
        </Box>
        <Box display='flex' pl={3} mt={3}>
          <Box display='flex' flexDirection='column'>
            <Typography variant='body1' color='secondary' fontFamily='CalibreMedium'>
              {permissions?.showTransactions ? 'BILLING' : 'DETAILS'}
            </Typography>
            {permissions?.showTransactions && Boolean(invoice) ? (
              <Typography variant='body2' color='textSecondary'>
                {`Balance due - ${formatCurrency({
                  amount: invoice.total - invoice.retainedTotal,
                  currency: 'USD',
                  showDecimal: true,
                })}`}
              </Typography>
            ) : null}
            <Link
              href='/details'
              onClick={() =>
                track('Billing - See Full Details', {
                  orderId: orderV2Data?.id,
                  confirmationId: orderV2Data?.confirmation_id,
                })
              }
            >
              See full details
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReservationDetailsDesktop;
